/**
 * The admin console shell.
 *
 * Simpler than the tenant app's: no organization switcher, because a platform
 * administrator works ACROSS organizations rather than inside one. A live
 * platform-health indicator sits in the top bar, since "is anything down" is
 * the question this audience asks most often.
 *
 * This console is an ORGANIZATION MANAGEMENT console. Its navigation reflects
 * that: organizations, the module catalog they draw on, the plans they sit on,
 * and the platform's own health (layouts/console.js).
 *
 * The same shell as NexERP's: a top bar with search (Ctrl K), create,
 * notifications, theme and account; the console's navigation beside the page;
 * a tab bar on a phone.
 */
import { Suspense, lazy, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { LoadingState } from '../components/States.jsx';
import { useAdminAuth } from '../context/AdminAuthContext.jsx';
import { useApi } from '../hooks/useApi.js';
import { useAppearance } from '../hooks/useAppearance.js';
import { tenantAppUrl } from '../lib/tenantApp.js';
import * as adminApi from '../services/index.js';
import { CONSOLE, activeItemIn } from './console.js';
import { ContextNav } from './ContextNav.jsx';
import { MobileNav } from './MobileNav.jsx';
import { ShellProvider } from './ShellContext.jsx';
import { ShortcutsDialog } from './ShortcutsDialog.jsx';
import { Topbar } from './Topbar.jsx';
import { useNavCollapsed, useWorkspacePrefs } from './workspacePrefs.js';

/**
 * The command center is only needed once someone opens it, so it is its own
 * chunk — fetched while the browser is idle after the shell mounts, so the
 * first Ctrl K is as instant as every later one.
 */
const loadCommandCenter = () => import('./CommandCenter.jsx');
const CommandCenter = lazy(() => loadCommandCenter().then((module) => ({ default: module.CommandCenter })));

/** What a lazily-loaded screen shows while its code arrives: the page's shape. */
function PageSkeleton() {
  return (
    <div className="page-skeleton" aria-hidden="true">
      <div className="skeleton" style={{ width: 120, height: 10 }} />
      <div className="skeleton" style={{ width: 260, height: 22, marginTop: 10 }} />
      <div className="page-skeleton-card">
        {[0, 1, 2, 3, 4, 5].map((row) => (
          <div key={row} className="skeleton" style={{ width: `${88 - ((row * 13) % 30)}%` }} />
        ))}
      </div>
    </div>
  );
}

/**
 * Live platform status. Polled at a modest interval — it is a background
 * indicator, not a monitoring system. The gateway answers 503 when something
 * is unreachable, and the body still carries the verdict.
 */
function usePlatformHealth() {
  const { data, refetch } = useApi(() => adminApi.servicesHealth().catch((err) => err?.details ?? null), []);

  // Through a ref: `refetch` is a new function on every render, and the shell
  // re-renders on every navigation — depending on it directly would restart
  // the minute each time, and a busy administrator would never see a poll.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  useEffect(() => {
    const timer = setInterval(() => refetchRef.current(), 60_000);
    return () => clearInterval(timer);
  }, []);

  return data;
}

function Shell() {
  const location = useLocation();
  const { pathname } = location;
  const { user, signOut } = useAdminAuth();
  const { setTheme, setDensity, density } = useAppearance();

  const [navOpen, setNavOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useNavCollapsed();
  const [pageTitle, setPageTitle] = useState(null);
  const prefs = useWorkspacePrefs(user?.id ?? null);

  const health = usePlatformHealth();

  // Refreshed on every navigation: one count query, so a decision made on the
  // requests page is reflected as soon as the administrator moves on.
  const pendingRequests = useApi(() => adminApi.pendingModuleRequestCount(), [pathname]);
  const moduleRequests = pendingRequests.data?.pending ?? 0;
  const counts = useMemo(() => ({ moduleRequests, health: health?.status }), [moduleRequests, health?.status]);

  // A route change closes every overlay and starts the page at the top. A
  // LAYOUT effect: it must run before the new page's header reports its title
  // (child passive effects run before a parent's), or it would wipe it.
  useLayoutEffect(() => {
    setNavOpen(false);
    document.body.style.overflow = '';
    window.scrollTo(0, 0);
    setPageTitle(null);
  }, [pathname]);

  // The page's own name, as its header states it, for the tab title and for
  // "Jump back in". Until a header reports one, a navigation destination goes
  // by its label; a detail page waits for its header, so an organization is
  // never remembered as "Organizations".
  const navItem = useMemo(() => {
    const item = activeItemIn(CONSOLE.items, pathname);
    return item && item.to === pathname ? item : null;
  }, [pathname]);
  const label = pageTitle ?? navItem?.label ?? null;

  useEffect(() => {
    document.title = [label, 'NexERP Admin'].filter(Boolean).join(' · ');
  }, [label]);

  const { recordVisit } = prefs;
  useEffect(() => {
    if (!label || pathname === '/') return;
    recordVisit({ to: pathname, label });
  }, [label, pathname, recordVisit]);

  // --- keyboard ---------------------------------------------------------------

  const toggleNavCollapsed = useCallback(() => setNavCollapsed(!navCollapsed), [navCollapsed, setNavCollapsed]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const mod = event.metaKey || event.ctrlKey;
      if (mod && !event.shiftKey && !event.altKey && event.key.toLowerCase() === 'k') {
        // Never over an open dialog: every Modal listens for Escape in the
        // capture phase, so the Escape that closed the palette would close
        // the dialog beneath it too — and with it a half-filled form.
        if (document.querySelector('.modal-backdrop')) return;
        event.preventDefault();
        setCommandOpen((open) => !open);
        return;
      }
      if (event.defaultPrevented || mod || event.altKey) return;
      const target = event.target;
      if (target?.closest?.('input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"]')) return;

      if (event.key === '/') {
        event.preventDefault();
        setCommandOpen(true);
      } else if (event.key === '?') {
        event.preventDefault();
        setShortcutsOpen(true);
      } else if (event.key === '[') {
        event.preventDefault();
        toggleNavCollapsed();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggleNavCollapsed]);

  // Warm the command center once the page has settled.
  useEffect(() => {
    const idle = window.requestIdleCallback ?? ((callback) => window.setTimeout(callback, 1500));
    const cancel = window.cancelIdleCallback ?? window.clearTimeout;
    const handle = idle(() => {
      loadCommandCenter().catch(() => {});
    });
    return () => cancel(handle);
  }, []);

  // The page eases in on every route change, without remounting anything.
  const pageRef = useRef(null);
  useEffect(() => {
    const node = pageRef.current;
    if (!node) return;
    node.classList.remove('page-enter');
    void node.offsetWidth;
    node.classList.add('page-enter');
  }, [pathname]);

  // Commands the command center offers besides pages and records: the same
  // preferences and actions the account menu has.
  const commands = useMemo(
    () => [
      { id: 'theme-light', label: 'Switch to light theme', keywords: 'appearance mode', icon: 'sun', hint: 'Preferences', run: () => setTheme('light') },
      { id: 'theme-dark', label: 'Switch to dark theme', keywords: 'appearance mode night', icon: 'moon', hint: 'Preferences', run: () => setTheme('dark') },
      { id: 'theme-system', label: 'Use the system theme', keywords: 'appearance mode auto', icon: 'monitor', hint: 'Preferences', run: () => setTheme('system') },
      {
        id: 'density',
        label: density === 'compact' ? 'Use comfortable tables' : 'Use compact tables',
        keywords: 'density rows spacing',
        icon: 'density',
        hint: 'Preferences',
        run: () => setDensity(density === 'compact' ? 'comfortable' : 'compact'),
      },
      {
        id: 'tenant-app',
        label: 'Open the tenant app',
        keywords: 'erp workspace nexerp organization users sign in',
        icon: 'external-link',
        hint: 'New tab',
        run: () => window.open(tenantAppUrl(), '_blank', 'noreferrer'),
      },
      { id: 'shortcuts', label: 'Keyboard shortcuts', keywords: 'help keys', icon: 'keyboard', hint: 'Help', run: () => setShortcutsOpen(true) },
      { id: 'sign-out', label: 'Sign out', keywords: 'log out logout exit', icon: 'log-out', hint: 'Account', run: signOut },
    ],
    [density, setDensity, setTheme, signOut]
  );

  const shell = useMemo(
    () => ({
      currentApp: CONSOLE,
      prefs,
      counts,
      health,
      openCommand: () => setCommandOpen(true),
      setPageTitle,
    }),
    [prefs, counts, health]
  );

  return (
    <ShellProvider value={shell}>
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <div className={`shell shell-has-nav ${navCollapsed ? 'shell-nav-collapsed' : ''}`}>
        <Topbar
          onOpenCommand={() => setCommandOpen(true)}
          onOpenNav={() => setNavOpen(true)}
          health={health}
          moduleRequests={moduleRequests}
          onShowShortcuts={() => setShortcutsOpen(true)}
        />

        <div className="shell-body">
          {navOpen && <div className="ctxnav-scrim" onClick={() => setNavOpen(false)} role="presentation" />}
          <ContextNav
            app={CONSOLE}
            collapsed={navCollapsed}
            onToggleCollapsed={toggleNavCollapsed}
            open={navOpen}
            onNavigate={() => setNavOpen(false)}
            counts={counts}
            pinned={prefs.pinned}
          />

          <div className="main">
            <main className="page page-enter" id="main" ref={pageRef} tabIndex={-1}>
              {/* Here rather than around the whole router: a screen whose code
                  is still downloading shows its skeleton INSIDE the shell,
                  instead of replacing the shell with a spinner. */}
              <Suspense fallback={<PageSkeleton />}>
                <Outlet />
              </Suspense>
            </main>
          </div>
        </div>

        <MobileNav
          onOpenCommand={() => setCommandOpen(true)}
          onOpenNav={() => setNavOpen(true)}
          moduleRequests={moduleRequests}
        />
      </div>

      {commandOpen && (
        <Suspense fallback={null}>
          <CommandCenter
            open={commandOpen}
            onClose={() => setCommandOpen(false)}
            pinned={prefs.pinned}
            recent={prefs.recent.filter((entry) => entry.to !== pathname)}
            commands={commands}
          />
        </Suspense>
      )}
      <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </ShellProvider>
  );
}

export function AdminLayout() {
  const { status, isAuthenticated } = useAdminAuth();
  const location = useLocation();

  if (status === 'loading') return <LoadingState label="Restoring your session…" />;
  if (!isAuthenticated) return <Navigate to="/login" replace state={{ from: location }} />;

  return <Shell />;
}
